import { models, stores } from '@kurtosys/app-start';
import { models as eventManagerModels, SourceEventManager } from '@kurtosys/event-manager';
import { EventTypes } from '@kurtosys/event-manager/dist/models/EventTypes.js';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { ISourceApplication } from '@kurtosys/types/eventBus/index.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration';
import { IInputs } from '../../../models/app/IInputs';
import { IComponentStyles, IConfiguration, TStoreContext } from '../../../models/app/index.js';
import { Feature } from '../../shared/Feature.js';
import { IAppComponents } from '../models/IAppComponents';

type EventSource = eventManagerModels.EventSource;
type EventAppInitialisedDetail = eventManagerModels.detail.EventAppInitialisedDetail
type EventAppInitPublish = eventManagerModels.EventPublish<EventAppInitialisedDetail>;
type IGrid = components.base.Grid.models.IGrid;
type IGridProps = components.base.Grid.models.IGridProps;
type IApplicationEmbedInput = components.base.ApplicationEmbed.models.IApplicationEmbedInput;

/* [Component: appStoreComponentImport] */

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

interface IAppSourceDetail { 
	source: EventSource;
	detail?: EventAppInitialisedDetail ;
}

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	@observable.ref appSources: Record<string, IAppSourceDetail> = {};
	@observable.ref rawGridProps: IGridProps | undefined;
	@observable.ref gridProps: IGridProps | undefined;
	appInitTimeoutFn: NodeJS.Timeout | undefined;

	get gridRenderTimeout() {
		return 15000; // TODO: add configuration
	}

	constructor(element: HTMLElement, url: string, storeContext: TStoreContext, manifest: IManifest) {
		super(element, url, storeContext, manifest, Feature, devConfig);

		makeObservable(this);
	}

	getAppSourceKeys() {
		return Object.keys(this.appSources);
	}

	async customInitializeBefore() {
		// once we have fetched config and before we fetch data, orchestrate grid props based on app embeds.
		this.setRawGridProps();
		if (this.appComponentConfiguration?.features?.hideEmptyElements === true) {
			this.eventBusStore.enableChildCallbackTracking();
			this.getAppSourceKeys().forEach((sourceKey) => {
				this.eventBusStore.eventManager.subscribe(sourceKey, {
					type: EventTypes.APP_INITIALIZED,
					listener: this.eventSource,
					onPublish: async (ev: EventAppInitPublish) => {
						const eventDetail = { ...ev.detail };
						const publishingSourceKey = SourceEventManager.generateKey(ev.source);
						if (!this.addDetailToSource(publishingSourceKey, eventDetail)) {
							const publishingSourceKeyWithoutAppid = SourceEventManager.generateKey(ev.source, true);
							this.addDetailToSource(publishingSourceKeyWithoutAppid, eventDetail);
						}
	
						// Once all apps have returned, re-render the grid removing apps that don't have data.
						if (this.getAppSourceKeys().every((sourceKey) => this.appSources[sourceKey].detail)) {
							if (this.appInitTimeoutFn) {
								clearTimeout(this.appInitTimeoutFn);
							}
							this.appInitTimeoutFn = setTimeout(this.initializeGridApp, 500);
						}
					}
				});
			});
		} else {
			if (this.appInitTimeoutFn) {
				clearTimeout(this.appInitTimeoutFn);
			}
			this.initializeGridApp();
		}
	}

	async customDidRender() {
		this.initializeGridAppEvents();
	}

	initializeGridAppEvents = () => {
		// Trigger app initialized is in a timeout to initialize the grid
		// when all apps have loaded or after 15 seconds
		this.appInitTimeoutFn = setTimeout(this.initializeGridApp, this.gridRenderTimeout);
	};

	initializeGridApp = () => {
		this.setGridProps();
		// if we are tracking child event callbacks, then the AppStoreBase will need to know once all child apps of the grid have initialized.
		this.eventBusStore.setAreChildCallbacksCompleted(true);
	};

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	async customInitializeAfter() {}

	/**
	 * Determine the event source key, based on Application Embed configuration (which can be converted to EventSource)
	 *
	 * @param componentProps
	 * @returns string
	 */
	getSourceKeyFromAppEmbed(componentProps: ISourceApplication) {
		const source: EventSource = {
			...componentProps,
		};
		return SourceEventManager.generateKey(source);
	}

	/**
	 * Add references for app embeds from grid config, converting them into EventSource objects, keyed by their event source keys
	 */
	@action
	addSourceFromAppEmbedConfig = (componentProps: ISourceApplication) => {		
		const sourceKey = this.getSourceKeyFromAppEmbed(componentProps);
		this.appSources = {
			...this.appSources,
			[sourceKey]: {
				source: {...componentProps},
			}
		} 
	};

	/**
	 * Find and update the application reference using teh event source key, adding the related event detail
	 */
	@action
	addDetailToSource = (sourceKey: string, eventDetail: EventAppInitialisedDetail) => {
		if (this.appSources[sourceKey]) {
			if (this.appSources[sourceKey].detail) {
				// already initialized
				this.log('success', {
					message: `Grid.addDetailToSource: ${sourceKey} already initialized`
				});
				return true;
			}
			const app = { ...this.appSources[sourceKey] };
			this.appSources = {
				...this.appSources,
				[sourceKey]: {
					...app,
					detail: eventDetail,
				}
			};
			this.log('success', {
				message: `Grid.addDetailToSource: ${sourceKey} initialized`
			});
			return true;
		}
		// source key not found
		this.log('warning', {
			message: `Grid.addDetailToSource: ${sourceKey} not found for init`
		});
		return false;
	};

	@computed
	get appEmbedsHaveData() {
		if (
			this.appComponentConfiguration &&
			this.appComponentConfiguration.features &&
			this.appComponentConfiguration.features.hideEmptyElements === true
		) {
			return this.getAppSourceKeys().some((sourceKey) => this.appSources[sourceKey].detail && this.appSources[sourceKey].detail.hasData);
		}
		return true;
	}

	@computed
	get hasData(): boolean {
		const configDefined = !!this.gridPropsConfiguration;
		const rawGridDefined = !!this.rawGridProps;
		const appEmbedsHaveData = this.appEmbedsHaveData;
		return appEmbedsHaveData || rawGridDefined || configDefined;
	}

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get gridPropsConfiguration(): IGridProps {
		return (this.appComponentConfiguration && this.appComponentConfiguration.gridProps) || {};
	}

	@computed
	get workingGridProps() {
		// The render attribute from the appParams is used to pass the render value to the application embed
		const render = this.appParamsHelper.rawAppParams?.render;
		return {
			render,
			...(this.isBootstrapped ? this.gridProps : this.rawGridProps),
		};
	}

	@action
	processGridProps(gridProps: IGridProps, processor: (grid: IGridProps) => IGridProps): IGridProps {
		const props: IGridProps = { ...gridProps };
		if (!common.commonUtils.isNullOrEmpty(props.breakpoints)) {
			props.breakpoints.forEach((breakpoint) => {
				if (breakpoint.props) {
					breakpoint.props = processor(breakpoint.props);
				}
			});
		}
		return processor(props);
	}

	@action
	setGridProps(): void {
		if (this.rawGridProps) {
			if (this.appComponentConfiguration?.features?.hideEmptyElements) {
				this.gridProps = this.processGridProps(this.rawGridProps, (gridProps: IGridProps) => {
					if (gridProps.grid) {
						gridProps.grid = this.filterRawGrid(gridProps.grid);
					}
					return gridProps;
				});
			} else {
				this.gridProps =  this.rawGridProps;
			}
		}
	}

	filterRawGrid(rawGrid: IGrid): IGrid {
		const grid = { ...rawGrid };
		const { rows = [] } = grid;
		const rowsToRemove: number[] = [];
		for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
			const row = rows[rowIndex];
			if (row) {
				const { columns = [] } = row;
				const columnsToRemove: number[] = [];
				for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
					const column = columns[columnIndex];
					if (column) {
						const { components = [] } = column;
						const componentsToRemove: number[] = [];
						for (let componentIndex = 0; componentIndex < components.length; componentIndex++) {
							const component = components[componentIndex];
							if (component) {
								const { key } = component;
								let { grid: subGrid } = component;
								if (key) {
									switch (component.key) {
										case 'base.applicationEmbed': {
											const targetSourceKey = this.getSourceKeyFromAppEmbed(component.props);
											const associatedAppEmbed = this.appSources[targetSourceKey];
											if (
												!associatedAppEmbed ||
												!associatedAppEmbed.detail ||
												!associatedAppEmbed.detail.hasData
											) {
												this.log('warning', {
													message:
														'removing app embed from grid, see detail for associated component props',
													detail: component.props,
												});
												componentsToRemove.push(componentIndex);
											}
											break;
										}
									}
								} else if (subGrid) {
									subGrid = this.filterRawGrid(subGrid);
									if (!subGrid.rows || subGrid.rows.length <= 0) {
										componentsToRemove.push(componentIndex);
									}
								}
							}
						}
						if (componentsToRemove.length > 0) {
							for (const id of componentsToRemove.reverse()) {
								grid.rows[rowIndex].columns[columnIndex].components.splice(id, 1);
								// delete grid.rows[rowIndex].columns[columnIndex].components[id];
							}
						}
						if (grid.rows[rowIndex].columns[columnIndex].components.length <= 0) {
							columnsToRemove.push(columnIndex);
						}
					}
				}
				if (columnsToRemove.length > 0) {
					for (const id of columnsToRemove.reverse()) {
						grid.rows[rowIndex].columns.splice(id, 1);
						// delete grid.rows[rowIndex].columns[id];
					}
				}
				if (grid.rows[rowIndex].columns.length <= 0) {
					rowsToRemove.push(rowIndex);
				}
			}
		}
		if (rowsToRemove.length > 0) {
			for (const id of rowsToRemove.reverse()) {
				grid.rows.splice(id, 1);
				// delete grid.rows[id];
			}
		}
		return grid;
	}

	@action
	setRawGridProps(): void {
		this.rawGridProps = this.processGridProps(
			common.commonUtils.deepMergeObjects({}, this.gridPropsConfiguration),
			(gridProps: IGridProps) => {
				gridProps = this.orchestrateRawGridProps(gridProps);
				gridProps.queryStore = this.queryStore;
				gridProps.loadUiIds = true;
				return gridProps;
			},
		);
		// if there are no application embeds to wait for, then continue the render of the grid
		if (this.getAppSourceKeys().length <= 0) {
			this.initializeGridApp();
		}
	}

	orchestrateRawGridProps = (gridProps: IGridProps) => {
		const gridPropsClone = { ...gridProps };
		let { grid } = gridPropsClone;
		if (grid) {
			grid = this.orchestrateRawGrid(grid);
		}
		return gridPropsClone;
	};

	orchestrateRawGrid(grid: IGrid, parentPositionKey?: string) {
		const inputs: Partial<IInputs> = this.appParamsHelper.inputs || {};
		const embedInputs: IApplicationEmbedInput[] = (Object.keys(inputs) as (keyof IInputs)[]).map((key) => {
			const value = inputs[key] as any;
			return {
				key,
				value,
			};
		});
		const { rows = [] } = grid;
		rows.forEach((row, rowIndex) => {
			if (row) {
				const { columns = [] } = row;
				columns.forEach((column, columnIndex) => {
					if (column) {
						const { components = [] } = column;
						components.forEach((component, componentIndex) => {
							if (component) {
								const { key, grid: subGrid } = component;
								const positionKey = this.getPositionKey(
									rowIndex,
									columnIndex,
									componentIndex,
									parentPositionKey,
								);
								if (key) {
									switch (component.key) {
										case 'base.applicationEmbed':
											component.props = this.orchestrateEmbed(
												component.props,
												'ksys-app-other',
												embedInputs,
											);
											component.props = {
												...component.props,
												forceScriptInject: true,
											};
											this.addSourceFromAppEmbedConfig({ forceScriptInject: true, ...component.props });
											break;
									}
								} else if (subGrid) {
									this.orchestrateRawGrid(subGrid, positionKey);
								}
							}
						});
					}
				});
			}
		});
		return grid;
	}

	getPositionKey(rowIndex: number, columnIndex: number, componentIndex: number, parentPositionKey?: string): string {
		const positionKeyParts = [`grid`, `row_${rowIndex}`, `col_${columnIndex}`, `com_${componentIndex}`];
		if (parentPositionKey) {
			positionKeyParts.unshift(parentPositionKey);
		}
		return positionKeyParts.join('.');
	}
}
